import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Check } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, FONT_SIZES, ON_COLOR, TYPOGRAPHY } from '../../theme';
import { AdaptiveSheet, Button } from '../common';
import { useAuthStore } from '../../store/authStore';
import { UserRole, USER_ROLE_META } from '../../types';
import { ROLE_ICONS, ROLE_ORDER } from '../../constants/roleIcons';

interface RoleSelectionBodyProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Lifted from the old forced-onboarding stakeholder-type screen. Now an
 * opt-in form reachable from /profile (via the `RoleSelectionSheet` wrapper
 * below) and, as of panel-system.md Phase 3B, as a PanelStack drill-in
 * inside Tour REDI (HowReDIWorksSheet) — pushed with no sheet of its own,
 * so the tour stays the only dialog on screen. Fire Scout stays locked-on;
 * phase-2 roles render but are tagged "Coming soon" — they still toggle for
 * now so the user expresses interest, mirroring the original screen's
 * behavior.
 *
 * No AdaptiveSheet/ScrollView here — the host (the wrapper below, or
 * whichever PanelStack view hosts this inside the tour) provides the single
 * scrollable region, same convention as DefensePlanAssignForm.web's
 * AssignRoot.
 */
export function RoleSelectionBody({ visible, onClose }: RoleSelectionBodyProps) {
  const { colors } = useTheme();
  const user = useAuthStore((s) => s.user);
  const updateRoles = useAuthStore((s) => s.updateRoles);

  const [selected, setSelected] = useState<Set<UserRole>>(
    () => new Set<UserRole>(user?.roles?.length ? user.roles : ['fire-scout'])
  );
  const [saving, setSaving] = useState(false);

  // Re-seed selection if the user opens the sheet again after a change.
  useEffect(() => {
    if (visible) {
      setSelected(new Set<UserRole>(user?.roles?.length ? user.roles : ['fire-scout']));
    }
  }, [visible, user?.roles]);

  const toggle = (role: UserRole) => {
    if (role === 'fire-scout') return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  };

  const handleSave = async () => {
    if (!user?.id) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const roles: UserRole[] = [
        'fire-scout',
        ...Array.from(selected).filter((r) => r !== 'fire-scout'),
      ];
      await updateRoles(roles);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = useMemo(() => selected.size, [selected]);

  return (
    <View style={styles.content}>
      <Text style={[styles.title, { color: colors.text }]}>Pick your roles</Text>
      <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
        Every REDI member is a Fire Scout. Tell us what else you do so REDI can show you the right
        lists and checklists.
      </Text>

      <View style={styles.options}>
        {ROLE_ORDER.map((role) => {
          const option = { role, icon: ROLE_ICONS[role] };
          const meta = USER_ROLE_META[role];
          const isSelected = selected.has(role);
          const locked = role === 'fire-scout';
          const phase2 = meta.phase >= 2;
          return (
            <Pressable
              key={option.role}
              onPress={() => toggle(option.role)}
              testID={`role-card-${option.role}`}
              style={[
                styles.card,
                {
                  borderColor: isSelected ? colors.primary : colors.border,
                  backgroundColor: isSelected ? colors.primarySoft : colors.surface,
                },
              ]}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: isSelected, disabled: locked }}>
              <View
                style={[
                  styles.iconWrap,
                  { backgroundColor: isSelected ? colors.primarySoft : colors.surfaceAlt },
                ]}>
                <option.icon size={22} color={isSelected ? colors.primary : colors.textSecondary} />
              </View>
              <View style={styles.cardText}>
                <View style={styles.labelRow}>
                  <Text style={[styles.cardLabel, { color: colors.text }]}>{meta.label}</Text>
                  {phase2 && (
                    <Text style={[styles.comingSoon, { color: colors.textTertiary }]}>
                      Coming soon
                    </Text>
                  )}
                </View>
                <Text style={[styles.cardBlurb, { color: colors.textSecondary }]}>
                  {meta.blurb}
                </Text>
              </View>
              <View
                style={[
                  styles.checkbox,
                  {
                    borderColor: isSelected ? colors.primary : colors.border,
                    backgroundColor: isSelected ? colors.primary : 'transparent',
                  },
                ]}>
                {isSelected && <Check size={14} color={ON_COLOR.text} />}
              </View>
            </Pressable>
          );
        })}
      </View>

      <View style={styles.footer}>
        <Button
          title={saving ? 'Saving…' : selectedCount > 1 ? `Save ${selectedCount} roles` : 'Save'}
          onPress={handleSave}
          variant="primary"
          size="lg"
          fullWidth
          disabled={saving}
          testID="roles-save"
        />
      </View>
      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="roles-sheet-end" style={styles.scrollSentinel} />
    </View>
  );
}

interface RoleSelectionSheetProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Sheet wrapper around `RoleSelectionBody` for the profile screen — a
 * single dialog on a full-page screen, which panel-system.md keeps as-is.
 * Unchanged behavior and testIDs from before the Phase 3B split.
 */
export function RoleSelectionSheet({ visible, onClose }: RoleSelectionSheetProps) {
  return (
    <AdaptiveSheet
      isOpen={visible}
      backdrop="light"
      onRequestClose={onClose}
      onDismiss={onClose}
      dialogWidth={520}>
      <AdaptiveSheet.ScrollView testID="roles-sheet">
        <RoleSelectionBody visible={visible} onClose={onClose} />
      </AdaptiveSheet.ScrollView>
    </AdaptiveSheet>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
  },
  title: {
    ...TYPOGRAPHY.statNumeral,
    letterSpacing: -0.3,
    marginBottom: SPACING.xs,
  },
  subtitle: {
    ...TYPOGRAPHY.bodySmall,
    lineHeight: 19,
    marginBottom: SPACING.lg,
  },
  options: { gap: SPACING.sm },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: SPACING.md,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1.5,
    gap: SPACING.md,
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: BORDER_RADIUS.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardText: { flex: 1 },
  labelRow: { flexDirection: 'row', alignItems: 'center', gap: SPACING.sm },
  cardLabel: {
    ...TYPOGRAPHY.h4,
    fontSize: FONT_SIZES.md,
  },
  comingSoon: {
    ...TYPOGRAPHY.labelSmall,
    letterSpacing: 0.6,
  },
  cardBlurb: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    marginTop: SPACING.lg,
  },
  scrollSentinel: {
    height: 1,
  },
});
